/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Mantine
import {
	Box,
	Group,
	Image,
	Radio as NativeRadio,
	Stack,
	Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

// Components
import { Button } from '@components/atoms/Button';
import { Radio } from '@components/atoms/Radio';
import { Select } from '@components/atoms/Select';
import { Text } from '@components/atoms/Text';
import { Dropzone } from '@components/molecules/Dropzone';
import { ModalError } from '@features/sourcing/productions/components/ModalError';

// Styles
import classes from './FormAddProduction.module.css';

// Utils
import {
	cloneDeep,
	convertArrayMeterPointsToLabelValue,
	convertArrayToLabelValue,
	detectSeparator,
	extractColumnNames,
} from '@utils/functions';
import {
	PRODUCTION_CSV_COLUMNS_OPTIONS,
	PRODUCTION_UNITS_OPTIONS,
} from '@utils/options';

// APIs
import { useImportProduction } from '@features/sourcing/productions/api/import-production';
import { useBrands } from 'shared/api/get-brands';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useMeterPoints } from 'shared/api/get-meter-points';
import { useSites } from 'shared/api/get-sites';

// Icons
import RemoveRowIcon from '@assets/icons/trash-remove-column.svg';

import useStore from '@store/index';
import { IconCirclePlus } from '@tabler/icons-react';
import { getUnitLabel } from '@utils/units';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { Divider } from '@components/atoms';
import { TProduction } from '@global-types/index';
import { randomId } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useProducers } from 'shared/api/get-producers';
import SingleFormProduction from './SingleFormProduction';

type TCsvColumnOption = {
	label: string;
	value: string;
	required: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const FormAddProduction = (props: {
	form: any;
	onCancel: () => void;
	onBack: () => void;
}) => {
	const { data: producersToAddProductions } =
		useProducers();
	const { data: brands } = useBrands();
	const { data: projects } = useMeasurabilities();
	const { data: sites } = useSites();
	const { data: meterPoints } = useMeterPoints();
	const [fileData, setFileData] = useState<any>();
	const [columns, setColumns] = useState<
		TCsvColumnOption[]
	>([]);
	const [columnsCSV, setColumnsCSV] = useState([]);
	const [
		hasMultipleMeterpoints,
		setHasMultipleMeterpoints,
	] = useState(false);
	const [delimiterCSV, setDelimiterCSV] = useState('');
	const [isShowModalErrors, setIsShowModalErrors] =
		useState(false);
	const [selectedColumnsCSV, setSelectedColumnsCSV] =
		useState([]);
	const {
		productionsImportErrors,
		setProductionsImportErrors,
		productionsPage,
		productionsPageSize,
		productionsAddType,
		setProductionsAddType,
		setProductionsDefaultSelectedFields,
	} = useStore();

	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const { t } = useTranslation();

	const importProduction = useImportProduction(
		productionsPage,
		productionsPageSize
	);

	const handleReload = () => {
		const formValues = props.form.getValues();
		const allKeys = Object.keys(formValues);

		const rowKeys = allKeys.filter((key) =>
			key.startsWith('row-')
		);

		setColumns([]);
		setColumnsCSV([]);
		setSelectedColumnsCSV([]);

		if (rowKeys && Array.isArray(rowKeys)) {
			rowKeys.map((_row) =>
				props.form.setFieldValue(_row, null)
			);
		}
	};

	useEffect(() => {
		if (props.form && props.form.errors) {
			const firstErrorPath = Object.keys(
				props.form.errors
			)[0];
			if (firstErrorPath)
				props.form.getInputNode(firstErrorPath)?.focus();
		}
	}, [props.form.errors]);

	useEffect(() => {
		const _options: TCsvColumnOption[] =
			PRODUCTION_CSV_COLUMNS_OPTIONS;
		setColumns(_options);
	}, []);

	useEffect(() => {
		if (productionsAddType == 'manually') {
			props.form.clearErrors();
			setColumnsCSV([]);
			const isMultipleMeterpoints =
				props.form.getInputProps('meterPoint').value ===
				'multiplemeterpoint';
			if (isMultipleMeterpoints)
				props.form.setValues({
					site: null,
					meterPoint: null,
				});
			handleReload();
		} else {
			setProductionsDefaultSelectedFields([]);
			props.form.clearErrors();
			props.form.setFieldValue('productions', [
				{
					id: randomId(),
					quantity: 0,
					startDate: null,
				},
			]);
		}
	}, [productionsAddType]);

	useEffect(() => {
		if (productionsImportErrors) {
			setIsShowModalErrors(true);
		}
	}, [productionsImportErrors]);

	useEffect(() => {
		if (importProduction.isSuccess) {
			props.onBack();
		}
	}, [importProduction.isSuccess, props]);

	useEffect(() => {
		if (selectedColumnsCSV) {
			props.form.clearErrors();
		}
	}, [selectedColumnsCSV]);

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const getObjectByValue = (
			objects: any[],
			targetValue: string
		): any | null => {
			return (
				objects.find(
					(obj) => obj.value === targetValue
				) || null
			);
		};

		const updateSelectOptions = (currentColumn: any) => {
			const tableauNonCommun = columnsCSV.filter(
				(obj1: any) => {
					return !selectedColumnsCSV.some(
						(obj2: any) => obj2.columnsCSV === obj1.value
					);
				}
			);

			const findItem: any = selectedColumnsCSV.find(
				(item: any) => item.value == currentColumn.value
			);

			if (findItem) {
				return [
					...tableauNonCommun,
					{
						label: findItem.columnsCSV,
						value: findItem.columnsCSV,
					},
				];
			}

			return tableauNonCommun;
		};

		function updateLabelBasedOnUnit(
			_options: any,
			unitProduction: any
		) {
			const cloneOptions = cloneDeep(_options);

			let unitLabel: string = '';

			switch (unitProduction) {
				case 'KWH':
					unitLabel = getUnitLabel('gCO2perKwh');
					break;
				case 'MWH':
					unitLabel = getUnitLabel('gCO2perKwh');
					break;
				case 'KG':
					unitLabel = getUnitLabel('gCO2perKg');
					break;
				case 'kg':
					unitLabel = getUnitLabel('gCO2perKg');
					break;
				case 't':
					unitLabel = getUnitLabel('gCO2perKg');
					break;
				case 'T':
					unitLabel = getUnitLabel('gCO2perKg');
					break;
				default:
			}
			const index = cloneOptions.findIndex(
				(option: any) =>
					option.label === 'conversionFactor'
			);
			if (index !== -1) {
				cloneOptions[index] = {
					..._options[index],
					label: `${cloneOptions[index].label} ${unitLabel}`,
				};
			}

			return cloneOptions;
		}

		const handleUpload = (file: File) => {
			setFileData(file);
			const reader = new FileReader();
			reader.onload = (e: any) => {
				const content = e.target.result;
				const _delimiter: any = detectSeparator(content);
				setDelimiterCSV(_delimiter);
				const cloneExtractHeaders = [
					...extractColumnNames(content),
				];
				const extractColumnNamesList: any =
					cloneExtractHeaders.map((item: any) => ({
						label: item.replace(/[\r\n]+|"/g, ''),
						value: item.replace(/[\r\n]+|"/g, ''),
					}));
				setColumnsCSV(extractColumnNamesList);

				const isMultipleMeterpoints =
					props.form.getInputProps('meterPoint').value ===
					'multiplemeterpoint';

				const _options: TCsvColumnOption[] =
					PRODUCTION_CSV_COLUMNS_OPTIONS.filter((item) =>
						isMultipleMeterpoints
							? true
							: item.value !== 'meterPoint'
					);

				const unitProduction = props.form.getInputProps(
					'unitProduction'
				).value;

				const _columns = updateLabelBasedOnUnit(
					_options,
					unitProduction
				);

				setColumns(_columns);
			};
			reader.readAsText(file);
		};

		const handleChangeSelect = (
			col: any,
			_option: any
		) => {
			props.form.setFieldValue(
				'row-' + col.value,
				_option
			);
			const _optionObject: any = getObjectByValue(
				PRODUCTION_CSV_COLUMNS_OPTIONS,
				col.value
			);
			const newSelectedColumns: any = [
				...selectedColumnsCSV,
			];
			const isExist = newSelectedColumns.find(
				(item: any) => item.label === _optionObject.label
			);

			if (isExist) {
				if (_option !== null) {
					newSelectedColumns.forEach((objet: any) => {
						if (objet.label === _optionObject.label) {
							objet.columnsCSV = _option;
						}
					});
				} else {
					const cloneSelectedColumns = [
						...selectedColumnsCSV,
					];
					const newSelectedColumns =
						cloneSelectedColumns.filter(
							(item: any) => item.value !== col.value
						);
					setSelectedColumnsCSV([...newSelectedColumns]);
				}
			} else {
				newSelectedColumns.push({
					..._optionObject,
					columnsCSV: _option,
				});
				setSelectedColumnsCSV(newSelectedColumns);
			}
		};

		const handleRemoveColumn = (col: any) => {
			const cloneColumns = [...columns];
			const _columns = cloneColumns.filter(
				(item) => item !== col
			);
			setColumns(_columns);
			const _columnsSelected = selectedColumnsCSV.filter(
				(item: any) => item.value !== col.value
			);

			setSelectedColumnsCSV(_columnsSelected);
		};

		const getUnitsOfProductions = (options: any) => {
			const _selectedSite = sites.find(
				(_site: any) =>
					_site.id ==
					props.form.getInputProps('site').value
			);

			if (_selectedSite && _selectedSite.unit == 'KWH') {
				return [
					{ label: 'kilowattheure', value: 'KWH' },
					{ label: 'mégawattheures', value: 'MWH' },
				];
			} else if (
				_selectedSite &&
				_selectedSite.unit == 'KG'
			) {
				return [
					{ label: 'Tonne ', value: 'T' },
					{ label: 'kilogramme ', value: 'KG' },
				];
			} else if (
				_selectedSite &&
				_selectedSite.unit == 'ITEM'
			) {
				return [{ label: 'item ', value: 'ITEM' }];
			} else return options;
		};

		const cleanProductions = (
			productions: TProduction[]
		) => {
			return productions.map((production: any) => {
				if (typeof production === 'object')
					for (const propName in production) {
						if (
							production[propName] === null ||
							production[propName] === '' ||
							production[propName] === 0 ||
							production[propName] === undefined
						) {
							delete production[propName];
						}
					}
				return production;
			});
		};

		const handleSubmitForm = () => {
			const body: FormData = new FormData();

			body.append('delimiter', delimiterCSV);

			const _meterpoint = meterPoints.find(
				(meterpoint: any) =>
					meterpoint.id ==
					props.form.getInputProps('meterPoint').value
			);

			if (_meterpoint)
				body.append(
					'unit',
					props.form.getInputProps('unitProduction').value
				);

			const isMultipleMeterpoints =
				props.form.getInputProps('meterPoint').value ===
				'multiplemeterpoint';

			if (!isMultipleMeterpoints) {
				body.append(
					'meterPoint',
					props.form.getInputProps('meterPoint').value
				);
			}

			if (isMultipleMeterpoints) {
				body.append(
					'unit',
					props.form.getInputProps('unitProduction').value
				);
				body.append(
					'site',
					props.form.getInputProps('site').value
				);
			}

			const hasStartDate =
				selectedColumnsCSV.filter(
					(objet: any) => objet.value === 'startDate'
				).length == 1;

			const hasQuantity =
				selectedColumnsCSV.filter(
					(objet: any) => objet.value === 'quantity'
				).length == 1;

			if (!hasStartDate) {
				props.form.setFieldError('entity-startDate', ' ');
				props.form.setFieldError(
					'row-startDate',
					'startDate is required'
				);
			}
			if (!hasQuantity) {
				props.form.setFieldError('entity-quantity', ' ');
				props.form.setFieldError(
					'row-quantity',
					'quantity is required'
				);
			}

			if (productionsAddType == 'manually') {
				body.delete('delimiter');
				body.append('delimiter', ',');
				body.append(
					'filename',
					`productions_manual_${dayjs().utc().toISOString()}.csv`
				);
				props.form.validate();

				const productionsList =
					props.form.getValues().productions;

				const updatedEnddateProductionsList =
					productionsList.map((item: any) => {
						const newStartDate = dayjs(
							item.startDate
						).format('YYYY-MM-DD');
						if (item.startDate && item.endDate) {
							const startDate = dayjs(item.startDate);
							const endDate = dayjs(item.endDate);
							const isSameDates = startDate.isSame(
								endDate,
								'day'
							);

							const newEndDate = endDate
								.add(isSameDates ? 1 : 0, 'day')
								.format('YYYY-MM-DD');

							return {
								...item,
								startDate: new Date(newStartDate),
								endDate: new Date(newEndDate),
							};
						}
						return {
							...item,
							startDate: new Date(newStartDate),
						};
					});

				const blobCsvData = new Blob(
					[
						convertToCSV(
							cleanProductions(
								updatedEnddateProductionsList
							)
						),
					],
					{ type: 'text/csv;charset=utf-8;' }
				);

				const namedFile = new File(
					[blobCsvData],
					`productions_manual_${dayjs().utc().toISOString()}.csv`,
					{ type: 'text/csv;charset=utf-8;' }
				);

				body.append('file', namedFile);

				if (Object.keys(props.form.errors).length === 0)
					importProduction.mutate(body);
				return;
			}

			if (hasStartDate && hasQuantity) {
				const cloneColumnsCSV = JSON.parse(
					JSON.stringify(columnsCSV)
				);
				const cloneSelectedColumnsCSV = JSON.parse(
					JSON.stringify(selectedColumnsCSV)
				);

				cloneColumnsCSV.forEach(
					(element: any, index: number) => {
						const valueToFind = element.value;
						let found = false;

						cloneSelectedColumnsCSV.forEach(
							(item: any) => {
								if (valueToFind === item.columnsCSV) {
									element.label = item.label;
									element.value = item.value;
									found = true;
								}
							}
						);
						if (!found) {
							element.label = 'ignore' + index;
							element.value = 'ignore' + index;
						}
					}
				);

				const parseHeaders = cloneColumnsCSV
					.map((_column: any) => _column.value)
					.toString();
				body.append('headers', parseHeaders);

				if (fileData) {
					const filename =
						fileData &&
						fileData.name &&
						fileData.name.replace(/\.[^/.]+$/, '');

					body.append('filename', filename);
					body.append('file', fileData);
				}

				importProduction.mutate(body);
			}
		};

		const convertToCSV = (data: any) => {
			if (!data.length) return '';

			const headers = Object.keys(data[0]);
			const headerRow = headers.join(',');
			const rows = data.map((row: any) =>
				headers
					.map(
						(header) =>
							`"${(row[header] ?? '')
								.toString()
								.replace(/"/g, '""')}"`
					)
					.join(',')
			);

			return [headerRow, ...rows].join('\n');
		};

		/* ******************************** CONSTANTS ******************************* */

		const valuesToCheck =
			props.form.getInputProps('meterPoint').value ===
			'multiplemeterpoint'
				? ['startDate', 'quantity', 'meterPoint']
				: ['startDate', 'quantity'];

		const containsAllRequiredObjects =
			valuesToCheck.every((value) =>
				selectedColumnsCSV.some(
					(item: any) => item.value === value
				)
			);

		const requiredFields = [
			'producer',
			'project',
			'site',
			'meterPoint',
			'unitProduction',
		];
		const isValidSelects = requiredFields.every((field) =>
			props.form.isValid(field)
		);
		const isEnableToAdd =
			containsAllRequiredObjects && isValidSelects;

		const {
			onChange: onChangeProducer,
			...producerProps
		} = props.form.getInputProps('producer');

		const handleChangeProducer = (value: string) => {
			props.form.setFieldValue('producer', value);
			if (props.form.getInputProps('project').value) {
				props.form.setFieldValue('project', null);
				props.form.setFieldValue('site', null);
				props.form.setFieldValue('meterPoint', null);
				props.form.setFieldValue('unitProduction', null);
			}
		};

		const { onChange: onChangeProject, ...projectProps } =
			props.form.getInputProps('project');

		const handleChangeProject = (value: string) => {
			props.form.setFieldValue('project', value);
			if (props.form.getInputProps('site').value) {
				props.form.setFieldValue('site', null);
				props.form.setFieldValue('meterPoint', null);
				props.form.setFieldValue('unitProduction', null);
			}
		};

		const { onChange: onChangeSite, ...siteProps } =
			props.form.getInputProps('site');

		const handleChangeSite = (value: string) => {
			const meterPointsSite = meterPoints.filter(
				(_meterpoint: any) => _meterpoint.site === value
			);

			if (
				meterPointsSite &&
				Array.isArray(meterPointsSite)
			)
				setHasMultipleMeterpoints(
					meterPointsSite.length > 1
				);

			props.form.setFieldValue('site', value);
			if (props.form.getInputProps('meterPoint').value) {
				props.form.setFieldValue('meterPoint', null);
				props.form.setFieldValue('unitProduction', null);
			}
		};

		const {
			onChange: onChangeMeterPoint,
			...meterPointProps
		} = props.form.getInputProps('meterPoint');

		const handleChangeMeterPoint = (value: string) => {
			props.form.setFieldValue('meterPoint', value);
			if (
				props.form.getInputProps('unitProduction').value
			) {
				props.form.setFieldValue('unitProduction', null);
			}
		};

		const modifyPlaceholder = (label: string) => {
			if (label === 'startDate') {
				return 'startDate ( yyyy-mm-dd )';
			}
			return label;
		};

		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			try {
				const isMultipleMeterpoints =
					props.form.getInputProps('meterPoint').value ===
					'multiplemeterpoint';

				const _options: TCsvColumnOption[] =
					PRODUCTION_CSV_COLUMNS_OPTIONS.filter((item) =>
						isMultipleMeterpoints
							? true
							: item.value !== 'meterPoint'
					);

				const unitProduction = props.form.getInputProps(
					'unitProduction'
				).value;

				const _columns = updateLabelBasedOnUnit(
					_options,
					unitProduction
				);
				setColumns(_columns);
			} catch (error) {
				console.error(error);
			}
		}, [props.form.getInputProps('meterPoint').value]);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			try {
				const isMultipleMeterpoints =
					props.form.getInputProps('meterPoint').value ===
					'multiplemeterpoint';

				const _options: TCsvColumnOption[] =
					PRODUCTION_CSV_COLUMNS_OPTIONS.filter((item) =>
						isMultipleMeterpoints
							? true
							: item.value !== 'meterPoint'
					);
				const unitProduction = props.form.getInputProps(
					'unitProduction'
				).value;

				const _columns = updateLabelBasedOnUnit(
					_options,
					unitProduction
				);

				setColumns(_columns);
			} catch (error) {
				console.error(error);
			}
		}, [
			props.form.getInputProps('unitProduction').value,
		]);

		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit((values: any) => {
					if (productionsAddType == 'manually')
						handleSubmitForm();
				})}
			>
				{/* Delete Multiple selection modal */}
				<ModalError
					type="delete"
					data={
						productionsImportErrors &&
						productionsImportErrors.data &&
						Array.isArray(productionsImportErrors.data) &&
						productionsImportErrors.data.length > 0 &&
						productionsImportErrors.data
					}
					message={
						productionsImportErrors &&
						productionsImportErrors?.message &&
						productionsImportErrors.message
					}
					onConfirm={() => {}}
					onClose={() => {
						setIsShowModalErrors(false);
						setProductionsImportErrors(null);
					}}
					onRemove={(_error) => {
						const updatedErrors =
							productionsImportErrors.data.filter(
								(_err: any) => _err.id !== _error.id
							);
						setProductionsImportErrors({
							...productionsImportErrors,
							data: updatedErrors,
						});
					}}
					opened={isShowModalErrors}
				/>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{t('productions.title')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('productions.addProduction')}
					</Text>
				</Box>

				{/* Row for Producer */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							size={isBigScreen ? 'lg' : 'sm'}
							label={t('global.producer')}
							withAsterisk
							data={convertArrayToLabelValue(
								producersToAddProductions
							)}
							placeholder={t(
								'placeholders.selectProducer'
							)}
							onChange={handleChangeProducer}
							{...producerProps}
						/>
					</Box>
				</Box>
				{/* Row for Project & Site */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled={!props.form.isValid('producer')}
							size={isBigScreen ? 'lg' : 'sm'}
							label={t('global.project')}
							withAsterisk
							data={convertArrayToLabelValue(
								projects.filter(
									(item: any) =>
										item.entity.id ==
										props.form.getInputProps('producer')
											.value
								)
							)}
							placeholder={t(
								'placeholders.selectProject'
							)}
							onChange={handleChangeProject}
							{...projectProps}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<Select
							disabled={!props.form.isValid('project')}
							visibleFrom="xxl"
							size="lg"
							label={t('global.site')}
							withAsterisk
							data={convertArrayToLabelValue(
								sites.filter(
									(item: any) =>
										item.project.id ==
										props.form.getInputProps('project')
											.value
								)
							)}
							placeholder={t('placeholders.selectSite')}
							onChange={handleChangeSite}
							{...siteProps}
						/>
						<Select
							disabled={!props.form.isValid('project')}
							hiddenFrom="xxl"
							size="sm"
							label={t('global.site')}
							withAsterisk
							data={convertArrayToLabelValue(
								sites.filter(
									(item: any) =>
										item.project.id ==
										props.form.getInputProps('project')
											.value
								)
							)}
							placeholder={t('placeholders.selectSite')}
							onChange={handleChangeSite}
							{...siteProps}
						/>
					</Box>
				</Box>

				{/* Row for Meter point & Unit of production */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled={!props.form.isValid('site')}
							size={isBigScreen ? 'lg' : 'sm'}
							label={t('global.meterPoint')}
							withAsterisk
							// eslint-disable-next-line no-sparse-arrays
							data={[
								...convertArrayMeterPointsToLabelValue(
									meterPoints.filter(
										(item: any) =>
											item.site ==
											props.form.getInputProps('site')
												.value
									)
								),
								...(hasMultipleMeterpoints
									? [
											{
												label: t('global.fromFile'),
												value: 'multiplemeterpoint',
											},
										]
									: []),
								,
							]}
							placeholder={t(
								'placeholders.selectMeterPoint'
							)}
							onChange={handleChangeMeterPoint}
							{...meterPointProps}
						/>
					</Box>

					<Box className={classes.containerInput}>
						<Select
							disabled={!props.form.isValid('meterPoint')}
							visibleFrom="xxl"
							size="lg"
							label={t('global.unitProduction')}
							withAsterisk
							data={getUnitsOfProductions(
								PRODUCTION_UNITS_OPTIONS
							)}
							placeholder={t(
								'placeholders.selectUnitProduction'
							)}
							onChange={() => {}}
							{...props.form.getInputProps(
								'unitProduction'
							)}
						/>
						<Select
							disabled={!props.form.isValid('meterPoint')}
							hiddenFrom="xxl"
							size="sm"
							label={t('global.unitProduction')}
							withAsterisk
							data={getUnitsOfProductions(
								PRODUCTION_UNITS_OPTIONS
							)}
							placeholder={t(
								'placeholders.selectUnitProduction'
							)}
							onChange={() => {}}
							{...props.form.getInputProps(
								'unitProduction'
							)}
						/>
					</Box>
				</Box>
				<NativeRadio.Group
					value={productionsAddType}
					name="favoriteFramework"
					withAsterisk
					onChange={setProductionsAddType}
				>
					<Group gap={'54px'}>
						<Radio
							variant="outline"
							size="sm"
							label={t('global.addManually')}
							value={'manually'}
						/>
						<Radio
							variant="outline"
							size="sm"
							label={t('global.addFromFile')}
							value={'fromFile'}
						/>
					</Group>
				</NativeRadio.Group>
				{productionsAddType == 'manually' ? (
					<Stack>
						{props.form
							.getValues()
							.productions.map(
								(item: any, index: number) => (
									<SingleFormProduction
										key={item.id}
										form={props.form}
										item={item}
										index={index}
									/>
								)
							)}
						<Box>
							<Button
								size="compact-md"
								color="blue"
								leftSection={<IconCirclePlus size={23} />}
								onClick={() =>
									props.form.insertListItem(
										'productions',
										{
											startDate: null,
											quantity: 0,
											id: randomId(),
										}
									)
								}
								variant="transparent"
							>
								{t('productions.addAnotherProduction')}
							</Button>
						</Box>
					</Stack>
				) : (
					<Group gap={'54px'}>
						<Dropzone
							handleUpload={handleUpload}
							handleReload={handleReload}
						/>
					</Group>
				)}

				<Box className={classes.containerMatching}>
					{columnsCSV &&
						columnsCSV.length > 0 &&
						Array.isArray(columns) &&
						columns.length > 0 && (
							<Box className={classes.rowMatchingTitles}>
								<Box className={classes.containerTitle}>
									{t('global.entities')}
								</Box>
								<Box className={classes.containerTitle}>
									{t('global.columnName')}
								</Box>
							</Box>
						)}

					{/* Row for matching */}
					{columnsCSV &&
						columnsCSV.length > 0 &&
						columns.map((col: any) => (
							<Box
								key={col.label}
								className={classes.rowMatching}
							>
								<Box
									className={clsx(
										classes.containerInput,
										props.form.getInputProps(
											'entity-' + col.value
										).error && classes.spacerError
									)}
								>
									<Select
										readOnly
										visibleFrom="xxl"
										size="lg"
										withAsterisk
										data={convertArrayToLabelValue(
											brands
										)}
										placeholder={modifyPlaceholder(
											col.label
										)}
										{...props.form.getInputProps(
											'entity-' + col.value
										)}
										withoutrightsection
									/>
									<Select
										readOnly
										hiddenFrom="xxl"
										size="sm"
										withAsterisk
										data={convertArrayToLabelValue(
											brands
										)}
										placeholder={modifyPlaceholder(
											col.label
										)}
										{...props.form.getInputProps(
											'entity-' + col.value
										)}
										withoutrightsection
									/>
								</Box>
								<Box className={classes.containerInput}>
									<Select
										disabled={
											Array.isArray(
												updateSelectOptions(col)
											) &&
											updateSelectOptions(col).length == 0
										}
										visibleFrom="xxl"
										size={isBigScreen ? 'lg' : 'sm'}
										withAsterisk
										data={updateSelectOptions(col)}
										placeholder=""
										onChange={(_option: any) => {
											handleChangeSelect(col, _option);
										}}
										error={
											props.form.getInputProps(
												'entity-' + col.value
											).error
												? col.value + ' is required'
												: ''
										}
										value={
											props.form.getInputProps(
												'row-' + col.value
											).value
										}
									/>
									<Select
										disabled={
											Array.isArray(
												updateSelectOptions(col)
											) &&
											updateSelectOptions(col).length == 0
										}
										error={
											props.form.getInputProps(
												'entity-' + col.value
											).error
												? col.value + ' is required'
												: ''
										}
										hiddenFrom="xxl"
										size="sm"
										withAsterisk
										data={updateSelectOptions(col)}
										placeholder={''}
										onChange={(_option: any) => {
											handleChangeSelect(col, _option);
										}}
										value={
											props.form.getInputProps(
												'row-' + col.value
											).value
										}
									/>
								</Box>
								{col.required == false && (
									<Image
										className={classes.removeRowIcon}
										src={RemoveRowIcon}
										onClick={() => {
											handleRemoveColumn(col);
										}}
									/>
								)}
							</Box>
						))}
				</Box>
				<Divider size={'xs'} />
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							type="submit"
							data-autofocus
							disabled={
								productionsAddType == 'manually'
									? false
									: !isEnableToAdd
							}
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={() => {
								if (productionsAddType !== 'manually') {
									handleSubmitForm();
								}
							}}
							loading={importProduction.isLoading}
						>
							{t('productions.addProduction')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
